import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CContainer, CSpinner, CButton, CCard, CCardBody } from '@coreui/react';
import api from '../api/axiosInstance';
import html2pdf from 'html2pdf.js';
import renderMathInElement from 'katex/contrib/auto-render';
import 'katex/dist/katex.min.css';

const DownloadPDF = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const pdfContainerRef = useRef(null);

  const [questionData, setQuestionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const fetchQuestionDetails = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/Content/GetQuestionById/${id}`);
        setQuestionData(res.data);
      } catch (err) {
        console.error("Error fetching question:", err);
        setError("Failed to load question details.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchQuestionDetails();
  }, [id]);

  // Math Equations auto-render after data load
  useEffect(() => {
    if (questionData && pdfContainerRef.current) {
      try {
        renderMathInElement(pdfContainerRef.current, {
          delimiters: [
            { left: '$$', right: '$$', display: true },
            { left: '$', right: '$', display: false },
            { left: '\\(', right: '\\)', display: false },
            { left: '\\[', right: '\\]', display: true }
          ],
          throwOnError: false
        });
      } catch (e) {
        console.error("KaTeX rendering error:", e);
      }
    }
  }, [questionData]);

  // Clean HTML Helper Function
  const cleanAndParseHtml = (rawHtml) => {
    if (!rawHtml) return '';

    // Fix relative image URLs
    let corrected = rawHtml.replace(
      /src="\/uploads\//g,
      'src="https://apiambipie.mohdwebsolution.in/uploads/'
    );

    const parser = new DOMParser();
    const doc = parser.parseFromString(corrected, 'text/html');

    // Remove inline fixed font-sizes that break layout
    const allElements = doc.querySelectorAll('*');
    allElements.forEach(el => {
      el.style.fontSize = '';
    });

    return doc.body.innerHTML;
  };

  const handleDownloadPDF = async () => {
    const element = pdfContainerRef.current;
    if (!element) return;

    setGenerating(true);

    const customId = questionData?.customQuestionId || `Question_${id}`;
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `${customId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  if (error || !questionData) {
    return (
      <CContainer className="text-center mt-5 py-5">
        <h4 className="text-danger mb-3">{error || "Data not found"}</h4>
        <CButton color="primary" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  const isSubjective = (questionData.questionType || questionData.QuestionType) === 'Subjective';

  const optionsList = [
    { label: 'A', val: questionData.optionA || questionData.OptionA },
    { label: 'B', val: questionData.optionB || questionData.OptionB },
    { label: 'C', val: questionData.optionC || questionData.OptionC },
    { label: 'D', val: questionData.optionD || questionData.OptionD },
    { label: 'E', val: questionData.optionE || questionData.OptionE }
  ].filter(opt => opt.val && opt.val.toString().trim() !== '');

  return (
    <CContainer className="py-4">
      {/* Top Action Buttons */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <CButton color="secondary" onClick={() => navigate(-1)}>← Back</CButton>
        <CButton 
          color="danger" 
          className="fw-bold px-4 text-white" 
          onClick={handleDownloadPDF}
          disabled={generating}
        >
          {generating ? '⏳ Generating PDF...' : '📄 Download PDF Now'}
        </CButton>
      </div>

      {/* PDF Target Document */}
      <CCard className="border shadow-sm p-4 bg-white" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <CCardBody ref={pdfContainerRef} style={{ fontFamily: 'Arial, sans-serif', color: '#333' }}>
          
          {/* Header */}
          <div style={{ borderBottom: '2px solid #002147', paddingBottom: '12px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ color: '#002147', margin: 0, fontWeight: 'bold' }}>AMBIPIE LEARNING PORTAL</h2>
                <p style={{ color: '#666', margin: 0, fontSize: '12px' }}>Verified Academic Core Syllabus Resource Repository</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <strong style={{ fontSize: '14px', color: '#002147' }}>
                  Question ID: {questionData.customQuestionId || `LAAC${id}`}
                </strong>
                <br />
                <span style={{ fontSize: '12px', color: '#555' }}>
                  Type: {questionData.questionType || 'Objective'}
                </span>
              </div>
            </div>
          </div>

          {/* Syllabus Metadata */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '13px', backgroundColor: '#f9f9f9', border: '1px solid #ddd' }}>
            <tbody>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd', width: '50%' }}>
                  <strong>Chapter:</strong> {questionData.chapterName || 'N/A'}
                </td>
                <td style={{ padding: '8px', border: '1px solid #ddd', width: '50%' }}>
                  <strong>Topic:</strong> {questionData.topicName || 'N/A'}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <strong>Exam Reference:</strong> {questionData.askedIn || 'N/A'} - {questionData.askedYear || questionData.year || 'N/A'}
                </td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <strong>Book Source:</strong> {questionData.bookReference || 'N/A'}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Question Statement */}
          <div style={{ marginBottom: '25px' }}>
            <h5 style={{ color: '#002147', fontWeight: 'bold', marginBottom: '10px' }}>QUESTION STATEMENT:</h5>
            <div 
              style={{ fontSize: '15px', lineHeight: '1.6', paddingLeft: '10px', borderLeft: '3px solid #002147' }}
              dangerouslySetInnerHTML={{ __html: cleanAndParseHtml(questionData.questionText || questionData.QuestionText) }}
            />
          </div>

          {/* Options */}
          {!isSubjective && (
            <div style={{ marginBottom: '25px' }}>
              <h5 style={{ color: '#002147', fontWeight: 'bold', marginBottom: '10px' }}>OPTIONS:</h5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {optionsList.map(opt => (
                  <div key={opt.label} style={{ border: '1px solid #e0e0e0', borderRadius: '4px', padding: '10px', backgroundColor: '#fff' }}>
                    <strong style={{ color: '#002147', marginRight: '6px' }}>({opt.label})</strong>
                    <span dangerouslySetInnerHTML={{ __html: cleanAndParseHtml(opt.val) }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Solution */}
          {(questionData.solution || questionData.Solution) && (
            <div style={{ marginTop: '30px', paddingTop: '15px', borderTop: '1px dashed #ccc' }}>
              <h5 style={{ color: '#198754', fontWeight: 'bold', marginBottom: '10px' }}>SOLUTION / PROOF:</h5>
              <div 
                style={{ fontSize: '14px', lineHeight: '1.6', backgroundColor: '#f4faf6', padding: '12px', borderRadius: '4px', borderLeft: '3px solid #198754' }}
                dangerouslySetInnerHTML={{ __html: cleanAndParseHtml(questionData.solution || questionData.Solution) }}
              />
            </div>
          )}

        </CCardBody>
      </CCard>

      <style>{`
        /* Fix images inside PDF canvas */
        img {
          max-width: 100% !important;
          height: auto !important;
          display: block;
          margin: 10px 0;
        }
        .katex-display {
          margin: 0.5em 0 !important;
        }
      `}</style>
    </CContainer>
  );
};

export default DownloadPDF;